import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Box, Grid, Typography, IconButton } from '@mui/material'
import { useTheme } from '@mui/styles'
import { transformStrToCapital } from 'utils/router'
import ButtonList from 'components/generic/buttons/ButtonList'
import AlertSnackbar from 'components/generic/snackbars'
import FormModal from 'components/generic/modals/form-modal/FormModal'
import {
  CreateFinishedGoodContainer,
  FinishedGoodCreatedContainer,
  NewPackingListContainer,
  NewRegradeContainer,
  NewRegradeContainerLegacy,
  PackingListCreatedContainer,
} from 'components/containers'
import NewCustomerContainer from 'components/containers/customers/new-customer/NewCustomerContainer'
import NewMaterialContainer from 'components/containers/commodities/new-material/NewMaterialContainer'
import NewCommodityContainer from 'components/containers/commodities/new-commodity/NewCommodityContainer'
import EditFormulaModal from 'components/containers/commodities/EditFormulaModal'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import ConfirmDialog from 'components/generic/modals/confirm-dialog/ConfirmDialog'
import { hideFormModal, setSnackBar, setShowSideMenuText } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import BenchmarkPricing from 'components/containers/commodities/benchmarks/BenchmarkPricing'
import LinkSalesOrder from 'components/containers/outbound-tickets/link-sales-order/LinkSalesOrder'
import SyncLogInfo from 'components/containers/ledger/components/SyncLogInfo'
import EquipmentSelector from 'components/containers/devices/EquipmentSelector'
import AddEditVehicleBlade from 'components/containers/inbound-tickets/components/AddEditVehicleBlade'
import InvoicePaymentBlade from 'components/containers/outbound-tickets/components/InvoicePaymentBlade'
import InvoiceConfirmationModal from 'components/containers/outbound-tickets/components/InvoiceConfirmationModal'
import InvoicePrintModal from 'components/containers/outbound-tickets/components/InvoicePrintModal'
import SelectAvailableTransfer from 'components/containers/inbound-tickets/select-available-transfer/SelectAvailableTransfer'
import MergeTicketsModal from 'components/containers/inbound-tickets/merge-tickets/MergeTicketsModal'
import CashCardModal from 'components/containers/inbound-tickets/cash-cards/CashCardModal'
import FreightLaneBlade from 'components/containers/freight/freight-lanes/FreightLaneBlade'
import ContainerAssetBlade from 'components/containers/assets/containers/ContainerAssetBlade'
import AssetSelectionBlade from 'components/containers/load-board/AssetSelectionBlade'
import DispatchTicketStatusBlade from 'components/containers/load-board/DispatchTicketContainer/DispatchTicketStatusBlade'
import LinkPurchaseOrder from 'components/containers/inbound-tickets/link-purchase-order/LinkPurchaseOrder'
import DispatchTicketPreviewModal from 'components/containers/load-board/components/DispatchTicketPreviewModal'
import SettingsBladeModal from 'components/layouts/settings/SettingsBladeModal'
import CreateFromDispatchModal from 'components/containers/inbound-tickets/select-dispatch/CreateFromDispatchModal'
import NewAdjustmentContainer from 'components/containers/inventory/adjustments/NewAdjustmentContainer'
import TruckAssetBlade from 'components/containers/assets/trucks/TruckAssetBlade'
import TrailerAssetBlade from 'components/containers/assets/trailers/TrailerAssetBlade'
import ManageDriversBlade from 'components/containers/load-board/ManageDriversBlade/ManageDriversBlade'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { Theme } from '@mui/material_modern/styles'
import PaymentRestrictionsBlade from 'components/containers/bills/components/PaymentRestrictionsBlade.tsx'
import DispatchTicketImagesBlade from '../containers/load-board/DispatchTicketContainer/DispatchTicketImagesBlade.tsx'
import AddEditUserBlade from 'components/containers/settings/users/AddEditUserBlade'
import ConfigureRoleBlade from 'components/containers/settings/roles-and-permissions/ConfigureRoleBlade'
import RouterBreadcrumbs from 'components/generic/breadcrumbs'
import MenuIcon from '@mui/icons-material/Menu'
import LinkDispatchToObtModal from 'components/containers/outbound-tickets/components/link-dispatch/LinkDispatchToObtModal'
import { useHotkeyNavigation } from 'utils/custom-hooks/useHotkeyNavigation'

// TODO move this to component if used outside the context of layout
const TitleAndMainActionBar = ({ textOrComponent, status, actions }) => {
  const theme: Theme = useTheme()
  const dispatch = useDispatch()
  const { sideBarWidth, showSideMenuText } = useAppSelector(state => state.ui)
  const { accessibleMenuOptions } = useHotkeyNavigation()

  const handleToggleNav = () => {
    window.localStorage.setItem('showSideMenuText', (!showSideMenuText).toString())
    dispatch(setShowSideMenuText(!showSideMenuText))
  }
  const useCategoryNav = localStorage.getItem('useCategoryNav') === 'true'

  return (
    <Box
      id="title-bar-header"
      p={2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        padding: `
          ${theme.spacing(3)}
          ${theme.spacing(3)}
          ${theme.spacing(2)}
          ${theme.spacing(3)}
        `,
        maxWidth: `calc(100vw - ${sideBarWidth}px)`,
      }}
      borderBottom={1}
      borderColor="divider"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {!showSideMenuText && useCategoryNav && !location.pathname.includes('/app/settings') && (
          <IconButton
            onClick={handleToggleNav}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: 'primary.lightBg',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {useCategoryNav && !location.pathname.includes('/app/settings') && (
          <RouterBreadcrumbs accessibleMenuOptions={accessibleMenuOptions} />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        }}
      >
        {typeof textOrComponent === 'string' ? (
          <Typography variant="h5" sx={{ flex: 1, minWidth: '300px' }}>
            {textOrComponent}
          </Typography>
        ) : (
          <>{textOrComponent}</>
        )}
        {status}
        <ButtonList buttonsData={actions} />
      </Box>
    </Box>
  )
}

const actionsPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    icon: PropTypes.node,
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    id: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
)

TitleAndMainActionBar.propTypes = {
  textOrComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  status: PropTypes.node,
  actions: actionsPropTypes,
}

TitleAndMainActionBar.defaultProps = {
  status: null,
  actions: [],
}

interface ContentLayoutProps {
  showTitleAndMainActionBar?: boolean
  title?: string
  actions?: any[]
  useRouterTitle?: boolean
  children?: ReactNode
}

export interface ModalBladeProps {
  onFormStateChange: (state: boolean) => void
  onHideModal: (config?: { skipBlocker?: boolean; hideAll?: boolean }) => void
}

const ContentLayout: React.FC<ContentLayoutProps> = ({
  showTitleAndMainActionBar = true,
  title = '',
  actions = [],
  useRouterTitle = false,
  children = null,
}) => {
  const theme: Theme = useTheme()
  const actionsRef = useRef(actions)
  const [pageTitle, setPageTitle] = useState(title)
  const [pageActions, setPageActions] = useState(actionsRef.current)
  const [pageStatus, setPageStatus] = useState()
  const [isDirty, setDirty] = useState({})
  const [showBlocker, setShowBlocker] = useState(false)
  const { snackbarType, snackbarMessage, formModalName } = useAppSelector(state => state.ui)
  const { enableDisplayRegradeInformation } = useAppSelector(state => state.featureFlags?.variables || {})

  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const setContainerTitle = useCallback(customPageTitle => {
    setPageTitle(customPageTitle)
  }, [])

  const setContainerStatus = useCallback(customPageStatus => {
    setPageStatus(customPageStatus)
  }, [])

  const setContainerActions = customActions => {
    setPageActions(customActions)
    actionsRef.current = customActions
  }

  const handleFormState = useCallback(isFormDirty => {
    setDirty(isFormDirty)
  }, [])

  const onHideModal = (config?: { skipBlocker?: boolean; hideAll?: boolean }) => {
    const { skipBlocker = false, hideAll = true } = config || {}
    if (!skipBlocker && isDirty) {
      setShowBlocker(true)
    } else {
      dispatch(hideFormModal(hideAll))
      const visibleFormModal = formModalName[formModalName.length - 1]
      if (visibleFormModal === FORM_MODALS.SYNC_LOG_INFO) {
        navigate('/app/ledger/sync-log/')
      }
    }
  }

  const handleConfirmBlocker = () => {
    setShowBlocker(false)
    setDirty(false)
    dispatch(hideFormModal(null as any))
    const visibleFormModal = formModalName[formModalName.length - 1]
    if (visibleFormModal === FORM_MODALS.BENCHMARK_PRICING) {
      navigate('/app/commodities')
    }
  }

  const handleHideBlocker = () => {
    setShowBlocker(false)
  }

  useEffect(() => {
    if (useRouterTitle) {
      setPageTitle(transformStrToCapital(pathname.split('/').slice(-1).pop(), ['-']))
    }
  }, [useRouterTitle])

  useEffect(() => {
    setPageActions(actionsRef.current)
  }, [actionsRef.current])

  return (
    <ContentLayoutContext.Provider
      value={{
        setContainerTitle,
        setContainerStatus,
        setContainerActions,
      }}
    >
      <ConfirmDialog
        open={showBlocker}
        title="Leave This Page"
        question="Are you sure you want to leave this page?"
        description="All unsaved data will be lost."
        confirmLabel="Yes, Leave page"
        onClose={handleHideBlocker}
        onSubmit={handleConfirmBlocker}
      />

      <Grid container flexDirection="column">
        {showTitleAndMainActionBar && (
          <Grid
            item
            style={{
              zIndex: 2,
              position: 'sticky',
              top: 0,
              background: theme.palette.background.default,
            }}
          >
            <TitleAndMainActionBar textOrComponent={pageTitle} status={pageStatus} actions={pageActions} />
          </Grid>
        )}

        <Grid item>{children ?? <Outlet />}</Grid>

        {/* Should define all form modals here */}
        {/* === Inventory Form Modals Begin === */}
        <FormModal modalName={FORM_MODALS.NEW_REGRADE} onHideModal={onHideModal}>
          {enableDisplayRegradeInformation ? (
            <NewRegradeContainer onFormStateChange={handleFormState} onHideModal={onHideModal} />
          ) : (
            <NewRegradeContainerLegacy onFormStateChange={handleFormState} onHideModal={onHideModal} />
          )}
        </FormModal>

        <FormModal modalName={FORM_MODALS.NEW_FINISHED_GOOD} onHideModal={onHideModal}>
          <CreateFinishedGoodContainer onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal
          modalName={FORM_MODALS.NEW_PACKING_LIST}
          onHideModal={() => {
            onHideModal()
            navigate(-1)
          }}
        >
          <NewPackingListContainer
            onFormStateChange={handleFormState}
            onHideModal={() => {
              onHideModal()
              navigate(-1)
            }}
          />
        </FormModal>

        <FormModal modalName={FORM_MODALS.PACKING_LIST_CREATED} onHideModal={onHideModal}>
          <PackingListCreatedContainer onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.FINISHED_GOOD_CREATED} onHideModal={onHideModal}>
          <FinishedGoodCreatedContainer onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.NEW_ADJUSTMENT} onHideModal={onHideModal} maxWidth={1400}>
          <NewAdjustmentContainer onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>
        {/* === Inventory Form Modals End === */}

        <FormModal modalName={FORM_MODALS.NEW_CUSTOMER} onHideModal={onHideModal}>
          <NewCustomerContainer onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.BENCHMARK_PRICING} onHideModal={onHideModal}>
          <BenchmarkPricing onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.NEW_MATERIAL} onHideModal={onHideModal} maxWidth={1400}>
          <NewMaterialContainer onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.NEW_COMMODITY} onHideModal={onHideModal} maxWidth={1400}>
          <NewCommodityContainer onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.LINK_PURCHASE_ORDER} onHideModal={onHideModal}>
          <LinkPurchaseOrder onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.LINK_SALE_ORDER} onHideModal={onHideModal}>
          <LinkSalesOrder onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.EQUIPMENT_SELECTOR} onHideModal={onHideModal} maxWidth={550}>
          <EquipmentSelector onFormStateChange={handleFormState} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.CREATE_CASH_CARD} onHideModal={onHideModal}>
          <CashCardModal onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.ADD_VEHICLE} onHideModal={onHideModal}>
          <AddEditVehicleBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>
        <FormModal modalName={FORM_MODALS.SYNC_LOG_INFO} onHideModal={onHideModal}>
          <SyncLogInfo onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.INVOICE_PAYMENT} onHideModal={onHideModal}>
          <InvoicePaymentBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.INVOICE_CONFIRMATION} onHideModal={onHideModal} modalType="center">
          <InvoiceConfirmationModal onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.INVOICE_PRINT} onHideModal={onHideModal} modalType="print">
          <InvoicePrintModal onFormStateChange={handleFormState} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.SETTINGS} onHideModal={onHideModal}>
          <SettingsBladeModal onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.SELECT_AVAILABLE_TRANSFER} onHideModal={onHideModal}>
          <SelectAvailableTransfer onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>
        <FormModal modalName={FORM_MODALS.MERGE_TICKETS} onHideModal={onHideModal}>
          <MergeTicketsModal onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.EDIT_FORMULA} onHideModal={onHideModal} maxWidth={1200}>
          <EditFormulaModal onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.FREIGHT_LANE_BLADE} onHideModal={onHideModal} maxWidth={1400}>
          <FreightLaneBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.CONTAINER_ASSET_BLADE} onHideModal={onHideModal}>
          <ContainerAssetBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.TRUCK_ASSET_BLADE} onHideModal={onHideModal}>
          <TruckAssetBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.TRAILER_ASSET_BLADE} onHideModal={onHideModal}>
          <TrailerAssetBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.MANAGE_DRIVERS_BLADE} onHideModal={onHideModal}>
          <ManageDriversBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.ASSET_SELECTION_BLADE} onHideModal={onHideModal}>
          <AssetSelectionBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.DISPATCH_TICKET_STATUS_BLADE} onHideModal={onHideModal} maxWidth={550}>
          <DispatchTicketStatusBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.DISPATCH_TICKET_IMAGES_BLADE} onHideModal={onHideModal} maxWidth={550}>
          <DispatchTicketImagesBlade onHideModal={onHideModal} />
        </FormModal>

        <FormModal
          modalName={FORM_MODALS.DISPATCH_TICKET_PREVIEW}
          onHideModal={onHideModal}
          modalType="center"
          maxWidth={380}
          height="unset"
        >
          <DispatchTicketPreviewModal onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.CREATE_FROM_DISPATCH} onHideModal={onHideModal}>
          <CreateFromDispatchModal onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={'link-dispatch-to-obt'} onHideModal={onHideModal}>
          <LinkDispatchToObtModal onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={FORM_MODALS.PAYMENT_RESTRICTIONS_BLADE} onHideModal={onHideModal} maxWidth={550}>
          <PaymentRestrictionsBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={'add-edit-user-blade'} onHideModal={onHideModal}>
          <AddEditUserBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        <FormModal modalName={'configure-role-blade'} onHideModal={onHideModal} maxWidth={550}>
          <ConfigureRoleBlade onFormStateChange={handleFormState} onHideModal={onHideModal} />
        </FormModal>

        {snackbarMessage && (
          <AlertSnackbar
            message={snackbarMessage}
            isOpen={!!snackbarMessage}
            severity={snackbarType}
            handleClose={() => dispatch(setSnackBar({ snackbarMessage: null }))}
          />
        )}
      </Grid>
    </ContentLayoutContext.Provider>
  )
}

export default ContentLayout
